import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getFeaturedItems, addFeaturedItem, deleteFeaturedItem, updateFeaturedItem } from '../../Redux/Actions/FeaturedItemActions';
import { Trash2, Plus, Image as ImageIcon, Star, Users, Edit, ExternalLink, Eye, EyeOff, Sparkles } from 'lucide-react';
import AdminNavbar from '../../Components/AdminNavbar';
import './ManageOffers.css';

const ManageOffers = () => {
    const dispatch = useDispatch();
    const { items, loading } = useSelector(state => state.featuredItemReducer);

    const [newItem, setNewItem] = useState({
        title: '',
        type: 'best_seller',
        imageUrl: '',
        subtitle: '',
        link: '',
        isActive: true
    });

    const [activeTab, setActiveTab] = useState('best_seller');
    const [editingId, setEditingId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        dispatch(getFeaturedItems(true));
    }, [dispatch]);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setNewItem({ ...newItem, imageUrl: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const resetForm = () => {
        setNewItem({
            title: '',
            type: activeTab,
            imageUrl: '',
            subtitle: '',
            link: '',
            isActive: true
        });
        setEditingId(null);
    };

    const handleEdit = (item) => {
        setEditingId(item._id);
        setNewItem({
            title: item.title,
            type: item.type,
            imageUrl: item.imageUrl,
            subtitle: item.subtitle || '',
            link: item.link || '',
            isActive: item.isActive !== false
        });
        setActiveTab(item.type);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleToggleActive = (item) => {
        dispatch(updateFeaturedItem(item._id, { ...item, isActive: !item.isActive }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (editingId) {
            dispatch(updateFeaturedItem(editingId, newItem));
            setSuccessMessage('Élément mis à jour avec succès !');
        } else {
            dispatch(addFeaturedItem(newItem));
            setSuccessMessage('Nouvel élément ajouté !');
        }
        resetForm();
    };

    const handleDelete = (id) => {
        if (window.confirm('Voulez-vous vraiment supprimer cet élément ?')) {
            dispatch(deleteFeaturedItem(id));
            if (editingId === id) resetForm();
        }
    };

    const filteredItems = items.filter(item => item.type === activeTab);

    return (
        <div className="min-h-screen bg-[#F8FAFC] manage-offers-container">
            <AdminNavbar title="Gestion du contenu vedette" />

            <div className="container mx-auto px-4 py-8">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Sparkles size={20} className="text-amber-500" />
                            <h1 className="text-2xl font-black text-slate-800 uppercase tracking-tight">À la Une</h1>
                        </div>
                        <p className="text-slate-500 text-sm font-medium">Contrôlez les sliders de votre application mobile</p>
                    </div>

                    {successMessage && (
                        <div className="bg-green-50 text-green-600 px-4 py-2 rounded-xl text-xs font-bold border border-green-100 animate-bounce">
                            {successMessage}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* LEFT: FORM (Compact) */}
                    <div className="lg:col-span-4">
                        <div className="glass-sidebar rounded-2xl p-6 sticky top-24 border border-slate-200">
                            <h2 className="text-xs font-black text-slate-800 uppercase tracking-widest mb-6 pb-2 border-b border-slate-100 flex items-center gap-2">
                                {editingId ? <Edit size={14} className="text-blue-600" /> : <Plus size={14} className="text-blue-600" />}
                                {editingId ? 'Modifier Élément' : 'Nouveau Contenu'}
                            </h2>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Type d'affichage</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            type="button"
                                            onClick={() => !editingId && setNewItem({ ...newItem, type: 'best_seller' })}
                                            className={`py-2 px-3 rounded-lg text-[9px] font-black border transition-all ${newItem.type === 'best_seller' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-400 border-slate-200'} ${editingId ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            BEST SELLERS
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => !editingId && setNewItem({ ...newItem, type: 'partner_offer' })}
                                            className={`py-2 px-3 rounded-lg text-[9px] font-black border transition-all ${newItem.type === 'partner_offer' ? 'bg-[#FF9933] text-white border-[#FF9933]' : 'bg-white text-slate-400 border-slate-200'} ${editingId ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            PARTENAIRES
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Titre Principal</label>
                                    <input
                                        type="text"
                                        placeholder="Titre accrocheur..."
                                        value={newItem.title}
                                        onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                                        required
                                        className="w-full px-4 py-2.5 rounded-xl custom-input bg-slate-50 text-xs font-bold outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Sous-titre / Badge</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Promo -20%, 50 DT..."
                                        value={newItem.subtitle}
                                        onChange={(e) => setNewItem({ ...newItem, subtitle: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl custom-input bg-slate-50 text-xs font-bold outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Lien (Route ou URL)</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: /rewards ou https://..."
                                        value={newItem.link}
                                        onChange={(e) => setNewItem({ ...newItem, link: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl custom-input bg-slate-50 text-xs font-mono text-blue-600 outline-none"
                                    />
                                </div>

                                <div className="pt-2">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-wider mb-2">Image d'illustration</label>
                                    <div className="flex items-center gap-4">
                                        <div className="w-16 h-16 rounded-xl bg-slate-50 border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                                            {newItem.imageUrl ? <img src={newItem.imageUrl} className="w-full h-full object-cover" alt="preview" /> : <ImageIcon size={20} className="text-slate-300" />}
                                        </div>
                                        <div className="flex-1">
                                            <input type="file" onChange={handleFileChange} accept="image/*" className="hidden" id="file-up" />
                                            <label htmlFor="file-up" className="w-full block py-2 text-center rounded-lg border border-slate-200 bg-white text-[9px] font-black text-slate-600 cursor-pointer hover:bg-slate-50 transition-all shadow-sm">
                                                CHOISIR PHOTO
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-2 pt-6">
                                    {editingId && (
                                        <button type="button" onClick={resetForm} className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase hover:bg-slate-100 rounded-xl transition-all">
                                            Annuler
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex-1 py-3 bg-slate-800 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-900 transition-all shadow-lg active:scale-95 disabled:opacity-50"
                                    >
                                        {editingId ? 'Mettre à jour' : 'Ajouter à la une'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* RIGHT: CONTENT LIST */}
                    <div className="lg:col-span-8 space-y-4">
                        <div className="flex items-center gap-1 p-1 bg-white rounded-xl shadow-sm border border-slate-200 w-fit">
                            <button
                                onClick={() => { setActiveTab('best_seller'); resetForm(); }}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-[10px] font-black transition-all tab-btn ${activeTab === 'best_seller' ? 'bg-slate-800 text-white active shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                <Star size={12} /> MEILLEURES VENTES
                            </button>
                            <button
                                onClick={() => { setActiveTab('partner_offer'); resetForm(); }}
                                className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-[10px] font-black transition-all tab-btn ${activeTab === 'partner_offer' ? 'bg-[#FF9933] text-white active shadow-md' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                <Users size={12} /> OFFRES PARTENAIRES
                            </button>
                        </div>

                        <div className="offers-table-wrapper">
                            <table className="offers-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '80px' }}>Visuel</th>
                                        <th>Contenu</th>
                                        <th className="hidden md:table-cell">Redirection</th>
                                        <th className="text-center">Affichage</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.length > 0 ? (
                                        filteredItems.map(item => (
                                            <tr key={item._id}>
                                                <td>
                                                    <img src={item.imageUrl} alt={item.title} className="offer-vignette" />
                                                </td>
                                                <td>
                                                    <div className="flex flex-col">
                                                        <span className="font-bold text-slate-800 text-sm leading-tight">{item.title}</span>
                                                        <span className="text-[11px] text-slate-400 font-medium">{item.subtitle || 'Sans description'}</span>
                                                    </div>
                                                </td>
                                                <td className="hidden md:table-cell">
                                                    {item.link ? (
                                                        <a href={item.link.startsWith('http') ? item.link : '#'} target="_blank" rel="noreferrer" className="text-[10px] text-blue-500 hover:underline flex items-center gap-1 font-mono">
                                                            <ExternalLink size={10} /> {item.link.length > 20 ? item.link.substring(0, 20) + '...' : item.link}
                                                        </a>
                                                    ) : <span className="text-[10px] text-slate-300 italic">Aucun lien</span>}
                                                </td>
                                                <td className="text-center">
                                                    <button
                                                        onClick={() => handleToggleActive(item)}
                                                        className={`status-pill ${item.isActive ? 'active' : 'hidden'}`}
                                                    >
                                                        {item.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                                                        {item.isActive ? 'Actif' : 'Masqué'}
                                                    </button>
                                                </td>
                                                <td className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <button onClick={() => handleEdit(item)} className="action-icon-btn btn-edit-hover text-slate-400" title="Modifier">
                                                            <Edit size={16} />
                                                        </button>
                                                        <button onClick={() => handleDelete(item._id)} className="action-icon-btn btn-delete-hover text-slate-400" title="Supprimer">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="empty-state-container">
                                                <div className="empty-state-icon flex justify-center">
                                                    {activeTab === 'best_seller' ? <Star size={48} /> : <Users size={48} />}
                                                </div>
                                                <p className="text-xs font-bold uppercase tracking-widest text-slate-300">Aucun élément disponible</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-blue-50/50 rounded-xl p-4 border border-blue-100 flex items-start gap-3">
                            <div className="bg-blue-500 text-white p-1 rounded-md mt-0.5">
                                <Sparkles size={12} />
                            </div>
                            <p className="text-[10px] text-blue-700 font-medium leading-relaxed">
                                <strong>Astuce :</strong> Utilisez des images de haute qualité et des titres courts. L'option "Masqué" vous permet de préparer vos campagnes à l'avance sans les publier immédiatement.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageOffers;
